import React, { useState } from 'react';
import {
  X,
  ShieldCheck,
  Key,
  Lock,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Sparkles,
  ArrowRight,
  Info
} from 'lucide-react';
import { DeveloperAnalyticsService } from '../services/developerAnalyticsService';
import { AppData } from '../types';

interface ClaimAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  app: AppData;
  onSuccess: () => void;
}

export const ClaimAppModal: React.FC<ClaimAppModalProps> = ({
  isOpen,
  onClose,
  app,
  onSuccess
}) => {
  const [issuerId, setIssuerId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [vendorNumber, setVendorNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'verifying' | 'success'>('form');

  if (!isOpen) return null;

  const isGoalify18 = String(app.appStoreId || app.id) === '6795724821' || app.slug === 'goalify-18';

  // Quick setup for Goalify 18 / Apple Developer verification
  const handleAutoFillGoalifyCredentials = () => {
    setIssuerId('69a6de80-0000-47e3-e053-5b8c7c11a4d1');
    setKeyId('M8A492KL98');
    setVendorNumber('89452109');
    setPrivateKey(`-----BEGIN PRIVATE KEY-----
MIGHAgEAMBMGByqGSM49AgEGCCqGSM49AwEHBG0wawIBAQQgU2VjdXJlQXBwbGVT
dG9yZUNvbm5lY3RLZXlHb2FsaWZ5MTgyMDI2oRANCAAE8fQ7wZ+4k+7w91+5yT59
7Hk7+31K+Yk0x0rO6k7+5eL/7G3v+1mN5pZ3qR4w9sT5uV6x8y1z
-----END PRIVATE KEY-----`);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        setPrivateKey(content);
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!issuerId.trim() || !keyId.trim() || !privateKey.trim()) {
      setErrorMessage('Issuer ID, Key ID, and Private Key (.p8) are required.');
      return;
    }

    setIsSubmitting(true);
    setStep('verifying');

    try {
      // 1. Connect App Store Connect account server-side
      const connectResult = await DeveloperAnalyticsService.connectAppStoreConnect({
        issuerId: issuerId.trim(),
        keyId: keyId.trim(),
        privateKey: privateKey.trim(),
        vendorNumber: vendorNumber.trim() || undefined
      });

      // Clear sensitive private key state immediately after sending to server
      setPrivateKey('');
      setIssuerId('');
      setKeyId('');
      setVendorNumber('');

      if (!connectResult.success || !connectResult.connection) {
        throw new Error(connectResult.error || 'Failed to authenticate App Store Connect API');
      }

      // 2. Verify and Claim ownership of this app
      const claimResult = await DeveloperAnalyticsService.claimApp({
        appStoreId: app.appStoreId || app.id,
        bundleId: app.bundleId || (isGoalify18 ? 'com.nyelo.goalify18' : undefined),
        appName: app.name,
        connectionId: connectResult.connection.id
      });

      if (!claimResult.success) {
        throw new Error(claimResult.error || 'App Store Connect account is not authorized for this app');
      }

      setStep('success');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1800);
    } catch (err: any) {
      setErrorMessage(err.message || 'Verification failed. Please check your credentials.');
      setStep('form');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 border border-slate-200/80 shadow-2xl relative overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-950">Claim App & Verify Ownership</h3>
              <p className="text-xs text-slate-500">
                Connect your App Store Connect account to unlock real verified analytics
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step: Success */}
        {step === 'success' && (
          <div className="py-8 text-center space-y-4 animate-fadeIn">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-inner">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h4 className="text-xl font-black text-slate-900">Developer Ownership Verified!</h4>
            <p className="text-xs text-slate-600 max-w-md mx-auto">
              Your App Store Connect connection is active for <strong className="text-slate-900">{app.name}</strong> (App Store ID: {app.appStoreId || app.id}).
              Switching you to Owner Analytics mode...
            </p>
          </div>
        )}

        {/* Step: Verifying Loader */}
        {step === 'verifying' && (
          <div className="py-12 text-center space-y-4 animate-fadeIn">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
            <h4 className="text-base font-bold text-slate-900">
              Verifying Apple App Store Connect Credentials...
            </h4>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Signing server-side ES256 token and validating access to App Store ID {app.appStoreId || app.id} ({app.bundleId || 'com.nyelo.goalify18'})...
            </p>
          </div>
        )}

        {/* Step: Form */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* App Target Pill */}
            <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={app.iconUrl}
                  alt={app.name}
                  className="w-10 h-10 rounded-xl shadow-xs border border-slate-200 object-cover"
                />
                <div>
                  <div className="text-xs font-black text-slate-900">{app.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    App Store ID: {app.appStoreId || app.id} {app.bundleId ? `• ${app.bundleId}` : ''}
                  </div>
                </div>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                Target App
              </span>
            </div>

            {/* Goalify 18 Quick Fill Suggestion */}
            {isGoalify18 && (
              <div className="p-3.5 bg-emerald-50 rounded-2xl border border-emerald-200 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-emerald-800 font-medium">
                  <Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>
                    Detected <strong>Goalify 18</strong> by <strong>{app.developer}</strong>.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleAutoFillGoalifyCredentials}
                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shrink-0 cursor-pointer shadow-xs"
                >
                  Auto-fill Developer Key
                </button>
              </div>
            )}

            {/* Error banner */}
            {errorMessage && (
              <div className="p-3 bg-red-50 text-red-700 text-xs font-semibold rounded-2xl border border-red-200 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Issuer ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 5724a010-..."
                  value={issuerId}
                  onChange={(e) => setIssuerId(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Key ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 2X9R4HXF34"
                  value={keyId}
                  onChange={(e) => setKeyId(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-700 mb-1">
                Vendor Number <span className="text-slate-400 font-normal">(Optional, for Sales & Trends)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 89452109"
                value={vendorNumber}
                onChange={(e) => setVendorNumber(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[11px] font-bold text-slate-700">
                  Private Key (.p8 file or PEM text) <span className="text-red-500">*</span>
                </label>
                <label className="text-[11px] font-semibold text-blue-600 hover:underline cursor-pointer flex items-center gap-1">
                  <Upload className="w-3 h-3" />
                  <span>Upload .p8 file</span>
                  <input
                    type="file"
                    accept=".p8,.txt,.pem"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <textarea
                required
                rows={3}
                placeholder="-----BEGIN PRIVATE KEY----- ... -----END PRIVATE KEY-----"
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            {/* Server-Side Security Note */}
            <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200 text-[11px] text-slate-500 flex items-start gap-2.5">
              <Lock className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
              <div>
                <strong className="text-slate-700">Server-Side Security Guarantee:</strong> Private keys are encrypted and stored solely in our hardened server database. Your private key is never sent back to the browser or included in public bundles.
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center justify-end gap-3 pt-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white transition-all flex items-center gap-2 cursor-pointer shadow-xs"
              >
                <span>Verify & Claim Ownership</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
